import React, { useEffect, useState, useCallback } from "react";
import { Button, Input } from "../../ui";
import { InviteService } from "../../../services/inviteService";
import type { InviteLink } from "../../../types/invite";

interface InviteLinkControlProps {
  campaignId: number;
}

const fullUrl = (path: string) => `${window.location.origin}${path}`;

const InviteLinkControl: React.FC<InviteLinkControlProps> = ({ campaignId }) => {
  const [link, setLink] = useState<InviteLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLink(await InviteService.getInviteLink(campaignId));
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      setLink(await InviteService.createInviteLink(campaignId));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await InviteService.revokeInviteLink(campaignId);
      setLink(null);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(fullUrl(link.url));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <h4 className="text-zinc-400 text-xs uppercase tracking-wide">Shareable invite link</h4>
      {link ? (
        <>
          <div className="flex gap-2">
            <Input
              type="text"
              value={fullUrl(link.url)}
              readOnly
              fullWidth
              data-testid="link-input"
            />
            <Button variant="primary" size="sm" onClick={copy} data-testid="copy-link">
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
          <p className="text-zinc-500 text-xs">
            Expires {new Date(link.expiresAt).toLocaleString()}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={generate}
              disabled={busy}
              data-testid="regenerate-link"
            >
              Regenerate
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={revoke}
              disabled={busy}
              data-testid="revoke-link"
            >
              Revoke
            </Button>
          </div>
        </>
      ) : (
        <Button
          variant="primary"
          size="sm"
          onClick={generate}
          disabled={busy}
          data-testid="generate-link"
        >
          {busy ? "Generating..." : "Generate invite link"}
        </Button>
      )}
    </div>
  );
};

export default InviteLinkControl;
